"use client";

import { useState, useCallback, useMemo } from "react";
import type { AttachedFile } from "./file-upload-handler";
import type { FileUploadHandlers } from "./file-upload-handler";

// Helper function to create AttachedFile from File
const createAttachedFile = (file: File): AttachedFile => ({
  id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  file,
  name: file.name,
  size: file.size,
  type: file.type,
});

// Helper function to process files into AttachedFile array
const processFiles = (files: FileList | File[]): AttachedFile[] => {
  return Array.from(files).map(createAttachedFile);
};

export function useFileUpload() {
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  // Optimized handlers using useCallback for stable references
  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newFiles = processFiles(files);
    setAttachedFiles(prev => [...prev, ...newFiles]);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragOver) {
      setIsDragOver(true);
    }
  }, [isDragOver]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only set to false if we're actually leaving the drop zone
    const relatedTarget = e.relatedTarget as Node;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const newFiles = processFiles(files);
      setAttachedFiles(prev => [...prev, ...newFiles]);
    }
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;
    if (!items) return;

    const files: File[] = [];
    
    // Use for...of for better performance than for loop
    for (const item of Array.from(items)) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      e.preventDefault();
      const newFiles = processFiles(files);
      setAttachedFiles(prev => [...prev, ...newFiles]);
    }
  }, []);

  // Memoize handlers to prevent unnecessary re-renders
  const handlers = useMemo<FileUploadHandlers>(() => ({
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
    onPaste: handlePaste,
  }), [handleDragOver, handleDragLeave, handleDrop, handlePaste]);

  return {
    attachedFiles,
    setAttachedFiles,
    isDragOver,
    setIsDragOver,
    handleFileSelect,
    handlers,
  };
}

